import React, { useMemo, useState, useSyncExternalStore } from "react";
import {
  visitModel,
  fmtElapsed,
  snoozeStillHere,
  stillHereConfirmText,
  subscribeStillHere,
  getStillHereVersion,
  STILL_HERE_SNOOZE_MINS,
} from "./visitflow";

// Visit timeline: Checked in → Roomed → With provider → Orders → Checked out,
// one status chip ("where · how long"), and the checkout reminder. All of it
// comes from visitModel (./visitflow.js, pure + node-tested), fed by the fields
// the dashboard API already returns: signintime journey stamps,
// visit_confirmation, room_no/token (manual check-ins), orders, lov,
// next_appointment and future_appt_max_id.

// One 30 s clock for the whole dashboard: every card and the queue toolbar's
// "to check out" count read the same instant, so they never disagree at a
// threshold. The interval runs only while something is subscribed. It is an
// instant; visitflow.js reads every stamp and "today" in clinic time
// (America/Chicago), so the browser's own zone does not matter.
const CLOCK_TICK_MS = 30000;
let clockNow = new Date();
let clockTimer = null;
const clockListeners = new Set();
const tickClock = () => {
  clockNow = new Date();
  clockListeners.forEach((listener) => listener());
};
const subscribeClock = (listener) => {
  clockListeners.add(listener);
  if (!clockTimer) clockTimer = setInterval(tickClock, CLOCK_TICK_MS);
  return () => {
    clockListeners.delete(listener);
    if (clockListeners.size === 0 && clockTimer) {
      clearInterval(clockTimer);
      clockTimer = null;
    }
  };
};
const subscribeNever = () => () => {};
const getClock = () => {
  // Nothing ticking (first mount, or after every card unmounted): catch up.
  // Compared with the same clock it stores (new Date), so the snapshot stays
  // stable between calls.
  const t = new Date();
  if (!clockTimer && t.getTime() - clockNow.getTime() >= CLOCK_TICK_MS) {
    clockNow = t;
  }
  return clockNow;
};

// live=false reads the shared clock without re-rendering on each tick.
export const useNow = (live = true) =>
  useSyncExternalStore(live ? subscribeClock : subscribeNever, getClock, getClock);

// The card's single visit model; the steps and the chip both render from it.
// The model also reads the "Still here" snoozes, so a snooze on any card
// (stillHereVersion) re-runs it at once instead of on the next tick.
export const useVisitFlow = (data) => {
  const now = useNow();
  const stillHereVersion = useSyncExternalStore(
    subscribeStillHere,
    getStillHereVersion,
    getStillHereVersion,
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => visitModel(data, now), [data, now, stillHereVersion]);
};

const CHIP_TONES = {
  neutral: "bg-[#ebf6f9] text-[#326d7e] font-medium",
  amber: "bg-[#fdf1ea] text-[#c05621] font-medium",
  red: "bg-[#fdecec] text-[#c53030] font-semibold",
  alert: "bg-[#fdf1ea] text-[#c05621] font-semibold ring-1 ring-[#f0b9a4]",
  muted: "bg-[#f3f5f8] text-[#4b5b6e] font-medium",
  flag: "bg-[#fdecec] text-[#c53030] font-semibold ring-1 ring-[#f5c2c0]",
};

const CheckSvg = () => (
  <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
);

const ChipIcon = ({ kind }) => {
  if (kind === "totals" || kind === "checked-out") {
    return (
      <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9.5" /><polyline points="8 12.5 11 15.5 16.5 9.5" /></svg>
    );
  }
  if (kind === "not-checked-out") {
    return (
      <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 21V4" /><path d="M5 4h11l-2 4 2 4H5" /></svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9.5" /><polyline points="12 6.5 12 12 15.5 13.8" /></svg>
  );
};

const CalendarSvg = () => (
  <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3.5" y="5" width="17" height="15.5" rx="2" /><path d="M3.5 10h17M8 3v4M16 3v4" /></svg>
);

const connectorClass = (steps, i, currentIdx) => {
  const next = steps[i + 1];
  if (next.done) return "bg-[#508fa4]";
  if (i + 1 === currentIdx) return "bg-[#f0b9a4]";
  // A skipped step the visit has already moved past keeps the line solid.
  if (next.skipped && (currentIdx === -1 || currentIdx > i + 1)) return "bg-[#508fa4]";
  return "bg-[#e8edf3]";
};

/* showSteps=false renders only the chip row: the card swaps the steps for the
   Next Appointment / No Follow Up pill once the visit is over, and the chip
   (totals, "Not checked out", "Check out?") still belongs under it.
   showFollowUp=false hides the small follow-up line where the card already
   shows the big No Follow Up pill.
   onMarkDoneCheckout(openOrders) -> Promise<boolean>: the "<order> pending ·
   still here?" prompt's "Mark done & check out" (after an inline confirm that
   names the orders); omitted when the user may not do it. "Still here" (the
   snooze) needs no privilege: it only hides the prompt in this tab. */
const VisitTimeline = ({
  data,
  onCheckout,
  onMarkDoneCheckout,
  checkingOut = false,
  showSteps = true,
  showFollowUp = true,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const model = useVisitFlow(data);
  // The prompt went away (order done elsewhere, snoozed): drop a half-open
  // confirm so it does not reappear with the next prompt.
  if (confirmOpen && !model?.stillHere) setConfirmOpen(false);

  if (!model) return null;
  const { steps, currentIdx, chip, checkoutDue, nextApptText, stillHere, openOrders } = model;
  if (!showSteps && !chip) return null;
  const canMarkDone = stillHere && typeof onMarkDoneCheckout === "function";
  const confirmMarkDone = async (e) => {
    e.stopPropagation();
    try {
      await onMarkDoneCheckout(openOrders);
    } finally {
      setConfirmOpen(false);
    }
  };

  // With steps on screen the outstanding checkout is shown on the checkout step
  // itself. With steps hidden there is nothing to hang it on, so the chip row
  // keeps it - that is the only place the "no follow-up" card can offer it.
  const checkoutInline = showSteps && checkoutDue;

  return (
    <div className="w-full mt-1 mb-1">
      {/* items-start, not items-center: the checkout column is taller than the
          others, and centring would drop every other circle below it. */}
      {showSteps && (
        <div className="flex items-start w-full">
          {steps.map((s, i) => {
            // An outstanding checkout is called out on the step it belongs to
            // rather than in the chip row: the circle pulses with a "?", and the
            // elapsed time and the action sit directly under it. Forced on for
            // the checkout step whether or not it is the current one, since an
            // open order can leave currentIdx pointing at Orders instead.
            const isCheckoutCue =
              checkoutInline && s.key === "checkout" && !s.done;
            return (
            <React.Fragment key={s.key}>
              <div
                className="flex flex-col items-center flex-none"
                title={s.skipped ? `${s.label}: not part of this visit` : undefined}
              >
                <div
                  className={`w-[22px] h-[22px] rounded-full flex items-center justify-center border-2 ${
                    isCheckoutCue
                      ? "bg-white border-[#e57f5c] text-[#e57f5c] checkout-pulse"
                      : s.done
                      ? "bg-[#508fa4] border-[#508fa4] text-white"
                      : s.skipped
                        ? "bg-[#f3f5f8] border-[#e5e7eb] text-[#9CA3AF]"
                        : i === currentIdx
                          ? "bg-white border-[#e57f5c] text-[#e57f5c]"
                          : "bg-white border-[#e5e7eb] text-transparent"
                  }`}
                >
                  {isCheckoutCue ? (
                    <span className="text-[13px] leading-none font-bold font-inter">
                      ?
                    </span>
                  ) : s.done ? (
                    <CheckSvg />
                  ) : s.skipped ? (
                    <div className="w-[8px] h-[2px] rounded bg-[#9CA3AF]" />
                  ) : i === currentIdx ? (
                    <div className="w-[8px] h-[8px] rounded-full bg-[#e57f5c]" />
                  ) : null}
                </div>
                {/* The checkout step's label reads "Checked out", which is not
                    true yet - while it is outstanding the action stands in for
                    it, and the label returns once the patient is checked out. */}
                {!isCheckoutCue && (
                  <div
                    className={`mt-1 text-[10px] font-inter whitespace-nowrap ${
                      s.done
                        ? "text-[#3f5566] font-medium"
                        : s.skipped
                          ? "text-[#9CA3AF]"
                          : i === currentIdx
                            ? "text-[#e57f5c] font-semibold"
                            : "text-[#9CA3AF]"
                    }`}
                  >
                    {s.label}
                  </div>
                )}
                {isCheckoutCue && (
                  <div className="mt-1 flex flex-col items-center gap-1">
                    {/* With the label suppressed the button is the only thing
                        naming this step, so a user who cannot check out still
                        needs a caption. */}
                    {!onCheckout && (
                      <span className="text-[10px] font-inter font-semibold text-[#e57f5c] whitespace-nowrap">
                        Check out?
                      </span>
                    )}
                    {chip && chip.mins !== null && chip.mins !== undefined && (
                      <span className="text-[10px] font-inter font-semibold text-[#e57f5c] whitespace-nowrap">
                        {fmtElapsed(chip.mins)}
                      </span>
                    )}
                    {onCheckout && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onCheckout();
                        }}
                        disabled={checkingOut}
                        title="Set the visit status to Checked Out"
                        className="inline-flex min-w-[92px] items-center justify-center gap-1 rounded-[5px] px-2 py-[2px] text-[11px] font-inter font-semibold text-white bg-[#508fa4] hover:bg-[#437c8f] disabled:opacity-60 disabled:cursor-wait focus:outline-none whitespace-nowrap"
                      >
                        {checkingOut ? "Checking out\u2026" : "Check out"}
                      </button>
                    )}
                  </div>
                )}
              </div>
              {i < steps.length - 1 && (
                <div
                  className={`flex-1 h-[2px] mx-1 mt-[10px] rounded ${connectorClass(
                    steps,
                    i,
                    currentIdx,
                  )}`}
                />
              )}
            </React.Fragment>
            );
          })}
        </div>
      )}
      {chip && !checkoutInline && (
        <div className="flex flex-wrap justify-center items-center gap-[6px] mt-1">
          <span
            title={chip.title || undefined}
            className={`inline-flex items-center gap-1 rounded-[5px] px-2 py-[2px] text-[11px] font-inter ${
              CHIP_TONES[chip.tone] || CHIP_TONES.neutral
            }`}
          >
            <ChipIcon kind={chip.kind} />
            {chip.text}
          </span>
          {checkoutDue && onCheckout && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onCheckout();
              }}
              disabled={checkingOut}
              title="Set the visit status to Checked Out"
              className="inline-flex items-center gap-1 rounded-[5px] px-2 py-[2px] text-[11px] font-inter font-semibold text-white bg-[#508fa4] hover:bg-[#437c8f] disabled:opacity-60 disabled:cursor-wait focus:outline-none"
            >
              {checkingOut ? "Checking out…" : "Check out"}
            </button>
          )}
          {canMarkDone && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setConfirmOpen(true);
              }}
              disabled={checkingOut || confirmOpen}
              aria-expanded={confirmOpen}
              title="Mark the open orders done and set the visit to Checked Out"
              className="inline-flex items-center gap-1 rounded-[5px] px-2 py-[2px] text-[11px] font-inter font-semibold text-white bg-[#508fa4] hover:bg-[#437c8f] disabled:opacity-60 focus:outline-none"
            >
              {checkingOut ? "Checking out…" : "Mark done & check out"}
            </button>
          )}
          {stillHere && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setConfirmOpen(false);
                snoozeStillHere(data);
              }}
              disabled={checkingOut}
              title={`The patient is still here: hide this for ${STILL_HERE_SNOOZE_MINS} minutes`}
              className="text-[11px] font-inter font-medium text-[#4b5b6e] underline underline-offset-2 hover:text-[#1f2d3d] disabled:opacity-60 focus:outline-none"
            >
              Still here
            </button>
          )}
        </div>
      )}
      {canMarkDone && confirmOpen && (
        <div
          role="group"
          aria-label="Confirm mark done and check out"
          onClick={(e) => e.stopPropagation()}
          className="mx-auto mt-1 max-w-[340px] rounded-[6px] border border-[#f0b9a4] bg-[#fdf6f3] px-2 py-[6px] text-center text-[11px] font-inter text-[#7c2d12]"
        >
          <div className="font-semibold">{stillHereConfirmText(openOrders)}</div>
          <div className="mt-[2px] text-[#9a3412]">
            {openOrders.length === 1
              ? "If it was not done, cancel it in Orders / Tasks instead."
              : "If any was not done, cancel it in Orders / Tasks instead."}
          </div>
          <div className="flex justify-center gap-2 mt-[6px]">
            <button
              type="button"
              onClick={confirmMarkDone}
              disabled={checkingOut}
              className="rounded-[5px] px-2 py-[2px] font-semibold text-white bg-[#c05621] hover:bg-[#9c4221] disabled:opacity-60 disabled:cursor-wait focus:outline-none"
            >
              {checkingOut ? "Working…" : "Confirm"}
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setConfirmOpen(false);
              }}
              disabled={checkingOut}
              className="rounded-[5px] px-2 py-[2px] font-medium text-[#4b5b6e] bg-white border border-[#e5e7eb] hover:bg-[#f3f5f8] disabled:opacity-60 focus:outline-none"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {checkoutDue && showFollowUp && (
        <div className="flex justify-center mt-1">
          {nextApptText ? (
            <span className="inline-flex items-center gap-1 rounded-[8px] border border-[#F5DED4] bg-[#FDF6F3] text-[#9A3412] px-2 py-[2px] text-[11px] font-inter font-semibold">
              <span className="text-[#C2410C] leading-none">
                <CalendarSvg />
              </span>
              {nextApptText}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-[8px] border border-[#FDE68A] bg-[#FFFBEB] text-[#92400E] px-2 py-[2px] text-[11px] font-inter font-semibold">
              <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="#B45309" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9.5" /><path d="M12 7.5v5.5M12 16.5v.01" /></svg>
              No follow-up booked
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default VisitTimeline;
